// Timezone helpers built on Intl only (no moment-timezone dependency), so the
// dashboard can render any IANA zone the browser supports.

export const TZ_STORAGE_KEY = 'castr.analytics.tz';

// Used when Intl.supportedValuesOf is unavailable (older Safari/Firefox).
const FALLBACK_TIME_ZONES = [
	'UTC',
	'Pacific/Honolulu', 'America/Anchorage', 'America/Los_Angeles', 'America/Denver', 'America/Phoenix',
	'America/Chicago', 'America/Mexico_City', 'America/New_York', 'America/Toronto', 'America/Bogota',
	'America/Lima', 'America/Caracas', 'America/Halifax', 'America/Santiago', 'America/Sao_Paulo',
	'America/Argentina/Buenos_Aires', 'Atlantic/Azores', 'Europe/London', 'Europe/Lisbon', 'Europe/Dublin',
	'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid', 'Europe/Rome', 'Europe/Amsterdam', 'Europe/Stockholm',
	'Europe/Warsaw', 'Europe/Athens', 'Europe/Kiev', 'Europe/Istanbul', 'Europe/Moscow', 'Africa/Cairo',
	'Africa/Johannesburg', 'Africa/Lagos', 'Africa/Nairobi', 'Asia/Dubai', 'Asia/Tehran', 'Asia/Karachi',
	'Asia/Kolkata', 'Asia/Dhaka', 'Asia/Bangkok', 'Asia/Jakarta', 'Asia/Singapore', 'Asia/Hong_Kong',
	'Asia/Shanghai', 'Asia/Manila', 'Asia/Seoul', 'Asia/Tokyo', 'Australia/Perth', 'Australia/Adelaide',
	'Australia/Sydney', 'Australia/Brisbane', 'Pacific/Auckland',
];

let cachedZones = null;

/**
 * All IANA zones the runtime knows about, sorted by current UTC offset.
 * Falls back to a curated list on engines without Intl.supportedValuesOf.
 */
export function getSupportedTimeZones() {
	if (cachedZones) return cachedZones;
	let zones = null;
	try {
		if (typeof Intl.supportedValuesOf === 'function') {
			zones = Intl.supportedValuesOf('timeZone');
		}
	} catch (e) {
		zones = null;
	}
	if (!Array.isArray(zones) || !zones.length) zones = FALLBACK_TIME_ZONES.slice();
	if (!zones.includes('UTC')) zones.push('UTC');
	const now = new Date();
	cachedZones = zones
		.filter(isValidTimeZone)
		.sort((a, b) => tzOffsetMinutes(a, now) - tzOffsetMinutes(b, now) || a.localeCompare(b));
	return cachedZones;
}

export function isValidTimeZone(tz) {
	if (!tz || typeof tz !== 'string') return false;
	try {
		new Intl.DateTimeFormat('en-US', { timeZone: tz });
		return true;
	} catch (e) {
		return false;
	}
}

export function getBrowserTimeZone() {
	try {
		const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
		return isValidTimeZone(tz) ? tz : 'UTC';
	} catch (e) {
		return 'UTC';
	}
}

export function loadStoredTimeZone() {
	try {
		const stored = window.localStorage.getItem(TZ_STORAGE_KEY);
		return isValidTimeZone(stored) ? stored : null;
	} catch (e) {
		return null;
	}
}

export function storeTimeZone(tz) {
	try {
		if (isValidTimeZone(tz)) window.localStorage.setItem(TZ_STORAGE_KEY, tz);
	} catch (e) {
		// Private mode or quota: ignore, the choice just will not persist.
	}
}

const partsFormatterCache = new Map();
function partsFormatter(tz) {
	if (!partsFormatterCache.has(tz)) {
		partsFormatterCache.set(tz, new Intl.DateTimeFormat('en-US', {
			timeZone: tz,
			hourCycle: 'h23',
			year: 'numeric', month: 'numeric', day: 'numeric',
			hour: 'numeric', minute: 'numeric', second: 'numeric',
		}));
	}
	return partsFormatterCache.get(tz);
}

/**
 * Calendar fields of `date` as seen in `tz`.
 */
export function getTzParts(date, tz) {
	const parts = partsFormatter(tz).formatToParts(date);
	const out = {};
	for (const p of parts) {
		if (p.type !== 'literal') out[p.type] = Number(p.value);
	}
	// Some engines report hour 24 for midnight with h23 in older versions.
	if (out.hour === 24) out.hour = 0;
	return out;
}

/**
 * Offset of `tz` from UTC in minutes at the given instant (east positive).
 */
export function tzOffsetMinutes(tz, date = new Date()) {
	const p = getTzParts(date, tz);
	const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
	return Math.round((asUtc - Math.floor(date.getTime() / 1000) * 1000) / 60000);
}

/**
 * "GMT-3", "GMT+5:30", "GMT+0".
 */
export function formatGmtOffset(tz, date = new Date()) {
	const mins = tzOffsetMinutes(tz, date);
	const sign = mins < 0 ? '-' : '+';
	const abs = Math.abs(mins);
	const h = Math.floor(abs / 60);
	const m = abs % 60;
	return `GMT${sign}${h}${m ? `:${m < 10 ? '0' + m : m}` : ''}`;
}

/**
 * "America/Halifax (GMT-3)".
 */
export function formatTimeZoneLabel(tz, date = new Date()) {
	return `${tz} (${formatGmtOffset(tz, date)})`;
}

/**
 * Instant of local midnight in `tz` for the calendar day containing `date`.
 */
export function startOfDayInTz(date, tz) {
	const p = getTzParts(date, tz);
	// First guess: treat the tz-local date as UTC and pull back by the offset.
	let guess = Date.UTC(p.year, p.month - 1, p.day) - tzOffsetMinutes(tz, date) * 60000;
	// DST edge: recompute the offset at the guessed instant.
	const offsetAtGuess = tzOffsetMinutes(tz, new Date(guess));
	guess = Date.UTC(p.year, p.month - 1, p.day) - offsetAtGuess * 60000;
	return new Date(guess);
}

/**
 * Instant for a wall-clock reading in `tz` (year, 1-based month, day, hour,
 * minute, second). Inverse of getTzParts; DST-aware via a second pass.
 */
export function zonedTimeToInstant({ year, month, day, hour = 0, minute = 0, second = 0 }, tz) {
	const wallAsUtc = Date.UTC(year, month - 1, day, hour, minute, second);
	let guess = wallAsUtc - tzOffsetMinutes(tz, new Date(wallAsUtc)) * 60000;
	guess = wallAsUtc - tzOffsetMinutes(tz, new Date(guess)) * 60000;
	return new Date(guess);
}

/**
 * A Date whose *local* getters (getHours, getDate, …) read as the wall clock
 * of `instant` in `tz`. Lets calendar UI built on local Date math operate in
 * another zone; convert back with wallClockToInstant before using the value.
 */
export function instantToWallClock(instant, tz) {
	const d = new Date(instant);
	if (!tz) return d;
	const p = getTzParts(d, tz);
	return new Date(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
}

export function wallClockToInstant(wall, tz) {
	const d = new Date(wall);
	if (!tz) return d;
	return zonedTimeToInstant({ year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate(), hour: d.getHours(), minute: d.getMinutes(), second: d.getSeconds() }, tz);
}

const displayFormatterCache = new Map();
function displayFormatter(tz, key, options) {
	const cacheKey = `${tz}|${key}`;
	if (!displayFormatterCache.has(cacheKey)) {
		displayFormatterCache.set(cacheKey, new Intl.DateTimeFormat('en-US', { timeZone: tz, ...options }));
	}
	return displayFormatterCache.get(cacheKey);
}

/**
 * Date/time formatting in the selected zone. `style`:
 *   'date'      -> "Sep 8"
 *   'dateYear'  -> "Sep 8, 2026"
 *   'time'      -> "17:05"
 *   'dateTime'  -> "Sep 8, 17:05"
 *   'full'      -> "Sep 8, 2026, 17:05"
 *   'iso'       -> "2026-09-08 17:05" (CSV friendly)
 */
export function formatInTz(date, tz, style = 'dateTime') {
	const d = date instanceof Date ? date : new Date(date);
	if (Number.isNaN(d.getTime())) return '';
	switch (style) {
		case 'date':
			return displayFormatter(tz, style, { month: 'short', day: 'numeric' }).format(d);
		case 'dateYear':
			return displayFormatter(tz, style, { month: 'short', day: 'numeric', year: 'numeric' }).format(d);
		case 'time':
			return displayFormatter(tz, style, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(d);
		case 'full':
			return displayFormatter(tz, style, {
				month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
			}).format(d);
		case 'iso': {
			const p = getTzParts(d, tz);
			const pad = (n) => (n < 10 ? `0${n}` : String(n));
			return `${p.year}-${pad(p.month)}-${pad(p.day)} ${pad(p.hour)}:${pad(p.minute)}`;
		}
		case 'dateTime':
		default:
			return displayFormatter(tz, 'dateTime', {
				month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
			}).format(d);
	}
}

/**
 * Axis/tooltip label for a bucket timestamp given the query interval.
 * DAY buckets are calendar days on the analytics side (UTC), so they are
 * labelled as UTC dates rather than shifted into the display zone, which
 * would otherwise move every day back by one for zones west of UTC.
 */
export function formatBucketLabel(timestamp, interval, tz) {
	if (interval === 'DAY') return formatInTz(timestamp, 'UTC', 'date');
	if (interval === 'HOUR') return formatInTz(timestamp, tz, 'dateTime');
	return formatInTz(timestamp, tz, 'time');
}

/**
 * Machine-friendly bucket label for exports: "2026-09-08" for DAY buckets,
 * "2026-09-08 17:00" (in tz) otherwise.
 */
export function formatBucketIso(timestamp, interval, tz) {
	if (interval === 'DAY') {
		const p = getTzParts(new Date(timestamp), 'UTC');
		const pad = (n) => (n < 10 ? `0${n}` : String(n));
		return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
	}
	return formatInTz(timestamp, tz, 'iso');
}
